import { startMSWWorker } from '~/api/msw/browser'
import { addNotification } from '~/cynqueModules/addNotification'

// Start (or tear down) the MSW service worker early (02. prefix) — before any
// plugin/page issues an HTTP request that mocks must intercept.
export default defineNuxtPlugin(async () => {
  console.log('Reading config using useRuntimeConfig.')
  const config = useRuntimeConfig()
  console.log('Config read:', config)
  console.log('config.public', config.public)
  // Read mockApi from `public` explicitly so this gate and isUsingMockApi()
  // (which reads $config.public.mockApi) read the exact same key, rather than
  // relying on useRuntimeConfig()'s root→public fallback proxy.
  console.log(`config.public.mockApi = ${config.public.mockApi}`)
  if (config.public.mockApi) {
    // A failed mock startup must NOT take the app down with it. This `await` was
    // unguarded, so any rejection from `worker.start()` — the worker script not
    // being served yet on a cold dev server, a registration error, a browser
    // blocking service workers — propagated out of the plugin and aborted Nuxt's
    // whole initialisation chain. Reproduced by blocking mockServiceWorker.js:
    // Vuetify's plugin then never ran ("Could not find defaults instance") and
    // the app rendered a COMPLETELY BLANK page with no error UI, which is
    // exactly what "the server came up but the app didn't" looks like.
    // Boot without mocks instead, and say so loudly in both the console and the
    // UI — running against the real API unawares is the other bad outcome.
    try {
      const { controlled } = await startMSWWorker(config.public.serverOrigin)
      if (!controlled) {
        // Mocks were asked for, the worker registered, but it is not CONTROLLING
        // this page — so every `/api` call falls straight through the dev server
        // to the real proxy and the app populates with LIVE data while looking
        // completely normal. That silence is the whole problem: the only way to
        // notice is to recognise real records. Say it out loud. Reloading is the
        // fix (a service worker claims on the next navigation), which is why
        // this has historically looked like "it works after a second refresh".
        console.error(
          `[plugins/02.msw.client.ts] Mocks are ENABLED but the service worker` +
            ` is not controlling this page. Serving REAL data. Reload to fix.`
        )
        addNotification({
          type: 'warning',
          message:
            'Mocks are enabled but the mock service worker is not controlling' +
            ' this page, so you are seeing REAL data. Reload the page.',
        })
      }
    } catch (error) {
      console.error(
        `[plugins/02.msw.client.ts] The mock API failed to start.` +
          ` Continuing WITHOUT mocks — requests will go to the real API.`,
        error
      )
      addNotification({
        type: 'warning',
        message:
          'The mock API failed to start, so this session is talking to the' +
          ' REAL API. Reload to try again.',
      })
    }
  } else if ('serviceWorker' in navigator) {
    // Mocks are off. Tear down any MSW service worker left registered by a
    // previous mocks-on session — it otherwise keeps intercepting same-origin
    // fetches (fonts and other assets included), so rendering silently depends
    // on whether mocks were ever enabled in this browser profile.
    const registrations = await navigator.serviceWorker.getRegistrations()
    const mockRegistrations = registrations.filter((registration) =>
      [registration.active, registration.installing, registration.waiting].some(
        (worker) => worker?.scriptURL.includes('mockServiceWorker.js')
      )
    )
    if (mockRegistrations.length) {
      // Destructive and easy to miss: this is the step that converts a
      // previously-mocked browser profile to live data. If `mockApi` is ever
      // read as falsey when it should not be, this line is the evidence.
      console.warn(
        `[plugins/02.msw.client.ts] mockApi is FALSE — unregistering` +
          ` ${mockRegistrations.length} mock service worker(s).` +
          ` This session will use the REAL API.`
      )
    }
    await Promise.all(
      registrations
        .filter((registration) =>
          [
            registration.active,
            registration.installing,
            registration.waiting,
          ].some((worker) => worker?.scriptURL.includes('mockServiceWorker.js'))
        )
        .map((registration) => registration.unregister())
    )
  }
})
